import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

export interface ConfirmOptions {
  title?: string;
  /** Der bisherige confirm()-Text; Zeilenumbrüche bleiben erhalten. */
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  defaultValue?: string;
  placeholder?: string;
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const DialogContext = createContext<{
  confirm: ConfirmFn;
  prompt: PromptFn;
} | null>(null);

type Pending =
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (ok: boolean) => void }
  | {
      kind: "prompt";
      opts: PromptOptions;
      resolve: (value: string | null) => void;
    };

/**
 * Ersatz für window.confirm()/window.prompt(): `await confirm("…")` liefert
 * true/false, `await prompt({…})` den Text oder null bei Abbruch – genau wie
 * vorher, nur als Dialog statt Browser-Popup.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState("");
  const current = useRef<Pending | null>(null);

  const open = useCallback((next: Pending) => {
    // Ein noch offener Dialog gilt als abgebrochen.
    cancel(current.current);
    current.current = next;
    setPending(next);
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (o) =>
      new Promise<boolean>((resolve) =>
        open({
          kind: "confirm",
          opts: typeof o === "string" ? { description: o } : o,
          resolve,
        }),
      ),
    [open],
  );

  const prompt = useCallback<PromptFn>(
    (o) =>
      new Promise<string | null>((resolve) => {
        setValue(o.defaultValue ?? "");
        open({ kind: "prompt", opts: o, resolve });
      }),
    [open],
  );

  function settle(ok: boolean) {
    const p = current.current;
    current.current = null;
    setPending(null);
    if (!p) return;
    if (!ok) return cancel(p);
    if (p.kind === "confirm") p.resolve(true);
    else p.resolve(value);
  }

  const opts = pending?.opts;

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(o) => !o && settle(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {opts?.title ?? "Bist du sicher?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {opts?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending?.kind === "prompt" && (
            <Textarea
              autoFocus
              rows={3}
              value={value}
              placeholder={pending.opts.placeholder}
              onChange={(e) => setValue(e.target.value)}
              aria-label={opts?.title ?? "Eingabe"}
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {opts?.cancelLabel ?? "Abbrechen"}
            </AlertDialogCancel>
            <AlertDialogAction
              variant={opts?.destructive ? "destructive" : "default"}
              onClick={() => settle(true)}
            >
              {opts?.confirmLabel ??
                (pending?.kind === "prompt" ? "OK" : "Bestätigen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  );
}

function cancel(p: Pending | null) {
  if (!p) return;
  if (p.kind === "confirm") p.resolve(false);
  else p.resolve(null);
}

function useDialogs() {
  const ctx = useContext(DialogContext);
  if (!ctx)
    throw new Error("useConfirm()/usePrompt() braucht einen <ConfirmProvider>");
  return ctx;
}

export function useConfirm(): ConfirmFn {
  return useDialogs().confirm;
}

export function usePrompt(): PromptFn {
  return useDialogs().prompt;
}
